import React, { useState } from 'react'
import { useTranslation } from 'react-i18next';
import { TextInput } from '@/components/common/text-input';
import { Dropdown } from "@/components/ui/modal";
import { TimeIcon } from '@/components/agent-dashboard/assets';
import { generateHourlyTimeSlots } from '@/utils/generate-hourly-time-slots';
import { TextButton } from '@/components/common/text-button';
import { cn } from '@/lib/utils';
import { ConfirmModal } from '@/components/common/confirm-modal';
import { agentInfoHooks } from '@/manager/manager/agent-workflow-subscription-manager';
import { LoadingOverlay } from '@/components/common/loading-overlay';
import { SubscriptionSettingsSkeleton } from './subscription-settings-skeleton';

/** 订阅设置 */
export const SubscriptionSettings: React.FC = () => {
  const { t } = useTranslation();

  const { agentSubscription, loading } = agentInfoHooks.useAgentWorkflowSubscriptionData();
  const { updateSubscription, onValueChange, loading: updateLoading } = agentInfoHooks.useUpdateAgentSubscription();
  const { isChanged } = agentInfoHooks.useAgentSubscriptionChanged();
  const { resetAgentSubscription } = agentInfoHooks.useAgentAction();
  const { next, showConfirmModal, setShowConfirmModal, isTriggeredByRouter, triggerManualConfirm } = agentInfoHooks.useListenerBeforeAction('subscription');

  agentInfoHooks.useListenerAfterAction('subscription');

  const [type] = useState('video');

  const handleTimeChange = (value: string) => {
    onValueChange('subscriptionTime', Number(value));
  };

  const handleFrequencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    onValueChange('subscriptionFrequency', Number(value));
  };

  const handleSave = async () => {
    await updateSubscription();
  }

  const handleCancel = () => {
    if (isChanged) {
      triggerManualConfirm();
    }
  }

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    resetAgentSubscription();
    if (isTriggeredByRouter) {
      next();
    }
  }

  return (
    <div className='relative w-full h-64'>
      <div className='text-lg font-semibold text-[rgba(16,24,40,1)] mb-6'>{t('agent_dashboard.subscription_settings.title')}</div>
      {loading && <SubscriptionSettingsSkeleton />}
      {!loading && <div className='flex space-x-4 pb-6 border-b border-[rgba(233,234,235,1)]'>
        <div>
          <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-3 inline-block'>{t('agent_dashboard.subscription_settings.time')}</label>
          <Dropdown
            value={String(agentSubscription?.subscriptionTime || 0)}
            prefixIcon={<TimeIcon className="size-5" />}
            options={generateHourlyTimeSlots()}
            width="22.13rem"
            className='h-11'
            onValueChange={handleTimeChange}
            disabled={updateLoading}
          />
        </div>
        <div className='w-[22.13rem]'>
          <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-3 inline-block'>
            {t('agent_dashboard.subscription_settings.frequency')}
            <span className='text-[rgba(117,66,247,1)]'>*</span>
          </label>
          <div className='relative flex items-center'>
            <TextInput
              type="text"
              value={agentSubscription?.subscriptionFrequency}
              className='w-[3.75rem]'
              onChange={handleFrequencyChange}
              disabled={updateLoading}
            />
            <span className='absolute left-18 text-sm font-medium text-[rgba(0,0,0,1)]'>{t('agent_dashboard.subscription_settings.items_day')}</span>
          </div>
        </div>
        <div>
          <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-3 inline-block'>{t('agent_dashboard.subscription_settings.type')}</label>
          <TextInput
            value={type}
            readOnly
            className='w-[22.13rem] border-none p-0'
          />
        </div>
      </div>}
      <div className='flex justify-end mt-5 space-x-3'>
        <TextButton
          label={t('agent_dashboard.confirm_modal.cancel')}
          onClick={handleCancel}
          className={cn(
            '!w-20 h-10 bg-white',
            'font-semibold text-sm !text-[rgba(65,70,81,1)]',
            'border border-[rgba(213, 215, 218, 1)] hover:bg-gray-50',
          )}
        />
        <TextButton
          label={t('agent_dashboard.confirm_modal.save')}
          onClick={handleSave}
          disabled={updateLoading || !isChanged}
          className={cn(
            '!w-[4.063rem] h-10 !bg-[rgba(117,66,247,1)]',
            'font-semibold text-sm text-white',
            'border-none border-[rgba(213, 215, 218, 1)] hover:!bg-[#7542f7eb]',
          )}
        />
      </div>
      <LoadingOverlay loading={updateLoading} />
      <ConfirmModal
        open={showConfirmModal}
        onOpenChange={setShowConfirmModal}
        title={t('agent_dashboard.confirm_modal.cancel_title')}
        description={t('agent_dashboard.confirm_modal.cancel_desc')}
        cancel={t('agent_dashboard.confirm_modal.cancel')}
        confirm={t('agent_dashboard.confirm_modal.confirm')}
        onConfirm={handleCancelConfirm}
      />
    </div>
  )
}
